// api/billingClient.js
import axios from 'axios';
import TokenService from '../services/token.service';

/**
 * Cliente HTTP configurado para Billing Service
 * (coins, tiers, balance, transactions, usage, org pool).
 *
 * Base URL: REACT_APP_BILLING_API_URL — apunta al custom domain
 * (`billing-dev.zenitailab.com`) que strippea el stage `v1`. Si en
 * algún entorno se configura el invoke URL crudo del API Gateway, el
 * env var debe incluir el `/v1` al final.
 *
 * Auth: Bearer JWT (preferido) — el `billing_service` también acepta
 * `X-Zenit-Api-Key` como fallback, pero la UI solo usa JWT.
 *
 * Header `x-account-id`: lo enviamos siempre que esté disponible en
 * `localStorage.currentAccountId` para mantener consistencia con el
 * resto de servicios account-scoped (mismo patrón de
 * connectionsClient). El billing_service no lo exige hoy en sus rutas
 * `/accounts/{id}/...` (el id ya va en path), pero enviarlo no hace
 * daño y deja la base lista por si se añaden rutas que lean el header.
 */
const billingClient = axios.create({
  baseURL: process.env.REACT_APP_BILLING_API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor:
 *   - Authorization: Bearer <jwt>
 *   - x-account-id: <currentAccountId>
 */
billingClient.interceptors.request.use(
  (config) => {
    const token = TokenService.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const accountId = localStorage.getItem('currentAccountId');
    if (accountId) {
      config.headers['x-account-id'] = accountId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor: logging de errores + dejar que el caller los
 * maneje (no redirige a /login automáticamente — eso lo hace
 * httpClient para auth_service; aquí preferimos que el caller decida
 * según el código del envelope canónico del billing_service).
 */
billingClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      console.error('Billing API Error:', {
        status: error.response.status,
        code: error.response.data?.detail?.code,
        message: error.response.data?.detail?.message,
        url: error.config?.url,
      });
    }
    return Promise.reject(error);
  }
);

/**
 * Normaliza el envelope de error canónico del billing_service:
 * `{ detail: { code, message, request_id, ...extras } }`
 *
 * Devuelve un objeto plano con los campos relevantes para el FE.
 * Ver `notes/billing-service/03-error-format.md` para la tabla
 * completa de códigos.
 */
export const normalizeBillingError = (error) => {
  const detail = error.response?.data?.detail;
  return {
    status: error.response?.status ?? 0,
    code: detail?.code ?? 'UNKNOWN_ERROR',
    message:
      detail?.message ??
      error.message ??
      'Unable to reach the billing service',
    requestId: detail?.request_id ?? null,
    extras: detail ?? null,
  };
};

export default billingClient;
